// @ts-strict-ignore
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ValidatingFormComponent } from '@/hybrid/formbuilder/formBuilder.module';
import { Field, useForm } from 'react-final-form';
import { getFormFieldProps, getValidationFunction } from '@/hybrid/formbuilder/formbuilder.utilities';
import classNames from 'classnames';
import _ from 'lodash';
import Select from 'react-select';
import { useShowError } from '@/hybrid/formbuilder/hooks/useShowError';
import { FormFieldWrapper } from '@/hybrid/formbuilder/FormFieldWrapper';

export interface SelectOption<T> {
  label: string;
  value: T;
}

export interface SelectIF extends ValidatingFormComponent<string> {
  component: 'SelectFormComponent';
  value: string;
  options: SelectOption<any>[];
  placeholder?: string;
  required?: boolean;
  popoverContent?: React.ReactNode;
  insideModal?: boolean;
  disabled?: boolean;
  searchable?: boolean;
  onTooltipClick?: () => void;
}

/**
 * Native select form component with optional tool-tip/popover
 */
export const SelectFormComponent: React.FunctionComponent<SelectIF> = (props) => {
  const {
    name,
    testId = 'select',
    label,
    options,
    validation,
    extendValidation,
    extraClassNames,
    customErrorText,
    placeholder,
    required,
    popoverContent,
    value,
    insideModal = false,
    disabled = false,
    searchable = false,
    tooltip,
    onTooltipClick,
  } = props;

  const defaultValidation = (value) => required && !value;

  const appliedValidation = getValidationFunction(defaultValidation, extendValidation, validation);

  const findSelectedLabel = () => _.find(options, (option) => option.value === value)?.label;

  const { t } = useTranslation();
  const formState = useForm().getState();
  const { showError, setShowError } = useShowError(false);
  const [selectedLabel, setSelectedLabel] = useState(findSelectedLabel());

  useEffect(() => {
    setSelectedLabel(findSelectedLabel() || selectedLabel);
  }, [options, value]);

  const getOptionLabel = (option: SelectOption<any>) => `${t(option.label)}`;

  const getOptionValue = (option: SelectOption<any>) => option.value;

  const handleChange = (onChangeFn) => (selected: SelectOption<any>) => onChangeFn(selected.value);

  const menuPosition = insideModal ? 'absolute' : 'fixed';

  const portalStyle = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

  return (
    <FormFieldWrapper
      testId={testId}
      label={label}
      wrapperClassNames="flexFillOverflow"
      extraClassNames={extraClassNames}
      showError={showError}
      customErrorText={customErrorText}
      popoverContent={popoverContent}
      tooltip={tooltip}
      onTooltipClick={onTooltipClick}>
      <Field name={name} validate={appliedValidation}>
        {({ input, meta }) => {
          const formFieldProps = getFormFieldProps(formState, input, meta, props);
          setShowError(formFieldProps.showError);

          return (
            <Select
              placeholder={t(placeholder)}
              className="reactSelectWrapper"
              classNamePrefix={classNames(formFieldProps.extraClassNames, 'react-select')}
              value={formFieldProps.value ? { value: formFieldProps.value, label: selectedLabel || '' } : undefined}
              options={options}
              getOptionValue={getOptionValue}
              getOptionLabel={getOptionLabel}
              onChange={handleChange(formFieldProps.onChange)}
              isSearchable={searchable}
              menuPortalTarget={document.body}
              menuPosition={menuPosition}
              isDisabled={disabled}
              styles={insideModal && portalStyle}
            />
          );
        }}
      </Field>
    </FormFieldWrapper>
  );
};
